import type { RoutingEntry, RoutingMap, RoutingStrategyMap } from '@/domain/api-keys'
import {
    KNOWN_ROUTE_CODES,
    RouteError,
    type CreateRouteInput,
    type Route,
    type RouteErrorCode,
    type UpdateRouteInput,
} from '@/domain/routes'
import type { Tier } from '@/domain/tiers'
import type { RoutesApi } from '@/ports/routes-api'

import { appendActiveTenant } from './_active-tenant'

/**
 * HttpRoutesApi : adapter fetch des endpoints /api/v1/routes (CRUD des profils
 * de routing réutilisables). Les mutations requièrent X-CSRF-Token. Le format
 * `routing` (map tier→entries) est identique à celui des API keys.
 */
export class HttpRoutesApi implements RoutesApi {
    private readonly baseURL: string

    constructor(baseURL = '') {
        this.baseURL = baseURL.replace(/\/+$/, '')
    }

    async list(tenantId?: string): Promise<Route[]> {
        // Sans tenant explicite : applique l'override super_admin du topbar.
        const params = new URLSearchParams()
        if (tenantId) params.set('tenant_id', tenantId)
        else appendActiveTenant(params)
        const qs = params.toString()
        const resp = await this.send(qs ? `/api/v1/routes?${qs}` : '/api/v1/routes', { method: 'GET' })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { items?: unknown }
        if (!Array.isArray(body.items)) {
            throw new RouteError('internal_error', 'Invalid /routes response shape')
        }
        return body.items.map(parseRoute)
    }

    async create(input: CreateRouteInput, csrfToken: string): Promise<Route> {
        const payload: Record<string, unknown> = {
            name: input.name,
            routing: serializeRouting(input.routing),
        }
        if (input.description) payload.description = input.description
        if (input.tenantId) payload.tenant_id = input.tenantId
        if (input.routingStrategies && Object.keys(input.routingStrategies).length > 0) {
            payload.routing_strategies = serializeStrategies(input.routingStrategies)
        }
        const resp = await this.send('/api/v1/routes', {
            method: 'POST',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify(payload),
        })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { route?: unknown }
        return parseRoute(body.route)
    }

    async update(id: string, input: UpdateRouteInput, csrfToken: string): Promise<Route> {
        const payload: Record<string, unknown> = {
            name: input.name,
            description: input.description,
            routing: serializeRouting(input.routing),
        }
        if (input.routingStrategies && Object.keys(input.routingStrategies).length > 0) {
            payload.routing_strategies = serializeStrategies(input.routingStrategies)
        }
        const resp = await this.send(`/api/v1/routes/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify(payload),
        })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { route?: unknown }
        return parseRoute(body.route)
    }

    async remove(id: string, csrfToken: string): Promise<void> {
        const resp = await this.send(`/api/v1/routes/${encodeURIComponent(id)}`, {
            method: 'DELETE',
            headers: csrfHeaders(csrfToken),
        })
        if (!resp.ok) throw await readError(resp)
    }

    private send(path: string, init: RequestInit): Promise<Response> {
        return fetch(this.baseURL + path, {
            ...init,
            credentials: 'include',
            cache: 'no-store',
        }).catch((cause: unknown) => {
            const message = cause instanceof Error ? cause.message : 'fetch failed'
            throw new RouteError('network_error', message)
        })
    }
}

function csrfHeaders(token: string): Record<string, string> {
    return { 'Content-Type': 'application/json', 'X-CSRF-Token': token }
}

function parseRoute(raw: unknown): Route {
    if (!raw || typeof raw !== 'object') {
        throw new RouteError('internal_error', 'Invalid route payload')
    }
    const obj = raw as Record<string, unknown>
    return {
        id: stringField(obj.id),
        tenantId: stringField(obj.tenant_id),
        name: stringField(obj.name),
        description: stringField(obj.description),
        routing: parseRouting(obj.routing),
        routingStrategies: parseStrategies(obj.routing_strategies),
        createdAt: stringField(obj.created_at),
    }
}

// Codec routing — identique à celui des API keys (format tier→entries partagé
// avec le backend). Dupliqué ici volontairement pour garder l'adapter autonome.
function parseRouting(raw: unknown): RoutingMap {
    if (!raw || typeof raw !== 'object') return {}
    const out: Record<string, RoutingEntry[]> = {}
    for (const [tier, entries] of Object.entries(raw as Record<string, unknown>)) {
        if (!Array.isArray(entries)) continue
        out[tier] = entries
            .map((e) => {
                const entry = e as Record<string, unknown>
                const re: { providerKeyId: string; model: string; weight?: number } = {
                    providerKeyId: stringField(entry.provider_key_id),
                    model: stringField(entry.model),
                }
                if (typeof entry.weight === 'number' && entry.weight > 0) re.weight = entry.weight
                return re
            })
            .filter((e) => e.providerKeyId && e.model)
    }
    return out as RoutingMap
}

function parseStrategies(raw: unknown): RoutingStrategyMap | undefined {
    if (!raw || typeof raw !== 'object') return undefined
    const out: Record<string, string> = {}
    for (const [tier, strategy] of Object.entries(raw as Record<string, unknown>)) {
        if (typeof strategy === 'string' && strategy) out[tier] = strategy
    }
    return Object.keys(out).length > 0 ? (out as RoutingStrategyMap) : undefined
}

type RoutingEntryWire = { provider_key_id: string; model: string; weight?: number }

function serializeRouting(routing: RoutingMap): Record<string, RoutingEntryWire[]> {
    const out: Record<string, RoutingEntryWire[]> = {}
    for (const tier of Object.keys(routing) as Tier[]) {
        const cascade = routing[tier]
        if (!cascade || cascade.length === 0) continue
        out[tier] = cascade.map((e) => {
            const wire: RoutingEntryWire = { provider_key_id: e.providerKeyId, model: e.model }
            if (e.weight && e.weight > 0) wire.weight = e.weight
            return wire
        })
    }
    return out
}

function serializeStrategies(strategies: RoutingStrategyMap): Record<string, string> {
    const out: Record<string, string> = {}
    for (const [tier, strategy] of Object.entries(strategies)) {
        if (typeof strategy === 'string' && strategy) out[tier] = strategy
    }
    return out
}

function stringField(v: unknown): string {
    return typeof v === 'string' ? v : ''
}

async function readError(resp: Response): Promise<RouteError> {
    if (resp.status === 401) {
        return new RouteError('unauthorized', 'Session expired', 401)
    }
    if (resp.status === 403) {
        try {
            const body = (await resp.json()) as { error?: { code?: unknown } }
            const code = typeof body.error?.code === 'string' ? body.error.code : ''
            if (code === 'csrf_required' || code === 'csrf_invalid' || code === 'tenant_scope') {
                return new RouteError(code as RouteErrorCode, code, 403)
            }
        } catch {
            // fallback
        }
        return new RouteError('forbidden', 'Forbidden', 403)
    }
    let body: unknown = null
    try {
        body = await resp.json()
    } catch {
        return new RouteError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const err = (body as { error?: { code?: unknown; message?: unknown } }).error
    if (!err || typeof err !== 'object') {
        return new RouteError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const rawCode = typeof err.code === 'string' ? err.code : 'unknown'
    const code: RouteErrorCode = (KNOWN_ROUTE_CODES as readonly string[]).includes(rawCode)
        ? (rawCode as RouteErrorCode)
        : 'unknown'
    const message = typeof err.message === 'string' ? err.message : `HTTP ${resp.status}`
    return new RouteError(code, message, resp.status)
}
